import React from 'react';
import {useExperiments} from '@wix/yoshi-flow-editor';
import {Totals, TotalsDataHook} from '../../../../common/components/Totals/Totals';
import {Text, TextPriority} from 'wix-ui-tpa/cssVars';
import {classes} from './TotalsSection.st.css';
import {ILocaleKeys, useLocaleKeys} from '../../../../locale-keys/LocaleKeys';
import {useControllerProps} from '../../Widget/ControllerContext';
import {shorten} from '../../../../domain/utils/shorten';
import {AppliedCouponModel} from '../../../../domain/models/checkout/AppliedCoupon.model';
import {SubscriptionFrequency, SubscriptionModel} from '../../../../domain/models/checkout/Subscription.model';
import {getTotal, hasFreeTrial, isPartiallyPaid} from '../../../../domain/utils/total.util';
import {SPECS} from '../../../../common/constants';

export enum TotalsSectionDataHooks {
  Section = 'TotalsSectionDataHooks.Section',
  TotalsSectionWrapper = 'TotalsSectionDataHooks.TotalsSectionWrapper',
  ShippingRowValue = 'TotalsSectionDataHooks.ShippingRowValue',
  TotalSection = 'TotalsSectionDataHooks.TotalSection',
  TotalRow = 'TotalsSectionDataHooks.TotalRow',
  PayNowLaterSection = 'TotalsSectionDataHooks.PayNowLaterSection',
  PayNowRow = 'TotalsSectionDataHooks.PayNowRow',
  PayAfterTrialRow = 'TotalsSectionDataHooks.PayAfterTrialRow',
  PayLaterRow = 'TotalsSectionDataHooks.PayLaterRow',
  SubtotalRow = 'TotalsSectionDataHooks.SubtotalRow',
  ShippingRow = 'TotalsSectionDataHooks.ShippingRow',
  TaxRow = 'TotalsSectionDataHooks.TaxRow',
  AggregatedTaxBreakdownRow = 'TotalsSectionDataHooks.AggregatedTaxBreakdownRow',
  AggregatedTaxBreakdownSection = 'TotalsSectionDataHooks.AggregatedTaxBreakdownSection',
  AdditionalFeeRow = 'TotalsSectionDataHooks.AdditionalFeeRow',
  AdditionalFeesSection = 'TotalsSectionDataHooks.AdditionalFeesSection',
  AppliedCouponRow = 'TotalsSectionDataHooks.AppliedCouponRow',
  GiftCardRow = 'TotalsSectionDataHooks.GiftCardRow',
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export const TotalsSection = () => {
  const {checkout} = useControllerProps().checkoutStore;
  const {canShipToDestination} = useControllerProps().deliveryMethodStore;
  const {checkoutSettings} = useControllerProps().checkoutSettingsStore;
  const localeKeys = useLocaleKeys();
  const {experiments} = useExperiments();

  const numberOfAdditionalFees = checkout.additionalFees.length;
  const shouldUseNewSubscriptionView = experiments.enabled(SPECS.UseNewSubscriptionView);
  const shouldSupportFreeTrial = experiments.enabled(SPECS.SupportFreeTrialLineItems) && hasFreeTrial(checkout);
  const isCheckoutPartiallyPaid = isPartiallyPaid(checkout);

  const getSubtotalRowTitleText = (): string => {
    if (checkoutSettings.taxOnProduct) {
      return localeKeys.checkout.order_summary.totals.items();
    }
    return localeKeys.checkout.order_summary.totals.subtotal();
  };

  const isFreeShippingCoupon = (coupon: AppliedCouponModel): boolean => coupon.couponType === 'FreeShipping';

  const getCouponRowValueText = (coupon: AppliedCouponModel): string => {
    if (isFreeShippingCoupon(coupon)) {
      return localeKeys.checkout.order_summary.totals.discount.free_delivery();
    }
    return `-${checkout.priceSummary.discount.formattedAmount}`;
  };

  const getShippingRowTitleText = (): string => {
    if (checkout.selectedShippingOption?.isPickup) {
      return localeKeys.checkout.orderSummary.totals.pickup.label();
    }
    return localeKeys.checkout.order_summary.totals.delivery();
  };

  const getShippingRowValueText = (): string => {
    if (!canShipToDestination) {
      return localeKeys.checkout.summary.shipping.noAmount();
    }
    if (
      checkout.priceSummary.shipping.amount === 0 ||
      (checkout.appliedCoupon && isFreeShippingCoupon(checkout.appliedCoupon))
    ) {
      return localeKeys.checkout.order_summary.totals.free();
    }
    return checkout.priceSummary.shipping.formattedAmount;
  };

  const renderAdditionalFees = () => {
    return numberOfAdditionalFees === 1 ? (
      <Totals.TotalsRow
        dataHook={TotalsSectionDataHooks.AdditionalFeeRow}
        title={checkout.additionalFees[0].name}
        value={checkout.additionalFees[0].priceBeforeTax.formattedAmount}
      />
    ) : (
      <Totals.TotalsSection
        title={localeKeys.checkout.additionalFees()}
        dataHook={TotalsSectionDataHooks.AdditionalFeesSection}>
        {checkout.additionalFees.map((fee) => (
          <Totals.TotalsRow
            dataHook={TotalsSectionDataHooks.AdditionalFeeRow}
            key={fee.code}
            title={fee.name}
            value={fee.priceBeforeTax.formattedAmount}
          />
        ))}
      </Totals.TotalsSection>
    );
  };

  const shouldShowTaxBreakdown = checkout.aggregatedTaxBreakdowns.length > 0;
  const numOfAggregatedTaxBreakdowns = checkout.aggregatedTaxBreakdowns.length;

  const renderTaxBreakdowns = () => {
    return numOfAggregatedTaxBreakdowns === 1 ? (
      <Totals.TotalsRow
        dataHook={TotalsSectionDataHooks.AggregatedTaxBreakdownRow}
        title={
          checkout.aggregatedTaxBreakdowns[0].taxName ||
          checkoutSettings.taxName ||
          localeKeys.checkout.order_summary.totals.tax()
        }
        value={checkout.aggregatedTaxBreakdowns[0].aggregatedTaxAmount.formattedAmount}
      />
    ) : (
      <Totals.TotalsSection
        title={localeKeys.checkout.order_summary.totals.tax()}
        dataHook={TotalsSectionDataHooks.AggregatedTaxBreakdownSection}>
        {checkout.aggregatedTaxBreakdowns.map((aggregatedTaxBreakdown, index) => (
          <Totals.TotalsRow
            dataHook={TotalsSectionDataHooks.AggregatedTaxBreakdownRow}
            key={index}
            title={
              aggregatedTaxBreakdown.taxName ||
              checkoutSettings.taxName ||
              localeKeys.checkout.order_summary.totals.tax()
            }
            value={aggregatedTaxBreakdown.aggregatedTaxAmount.formattedAmount}
          />
        ))}
      </Totals.TotalsSection>
    );
  };

  const renderPayNowPayLater = () => (
    <Totals.TotalsSection dataHook={TotalsSectionDataHooks.PayNowLaterSection}>
      <Totals.TotalsRow
        dataHook={TotalsSectionDataHooks.PayNowRow}
        title={() => (
          <Text className={classes.totalRowText} data-hook={TotalsDataHook.TotalsRowTitle}>
            {localeKeys.checkout.orderSummary.amountDueNow.label()}
          </Text>
        )}
        value={() => <Text className={classes.totalRowText}>{checkout.payNowTotalAfterGiftCard.formattedAmount}</Text>}
      />
      {shouldSupportFreeTrial && (
        <Totals.TotalsRow
          dataHook={TotalsSectionDataHooks.PayAfterTrialRow}
          title={() => (
            <Text data-hook={TotalsDataHook.TotalsRowTitle}>
              {localeKeys.checkout.pricingPlans.afterFreeTrial.label()}
            </Text>
          )}
          value={() => (
            <Text className={classes.totalRowText}>{checkout.payAfterFreeTrial?.total.formattedAmount}</Text>
          )}
        />
      )}
      {isCheckoutPartiallyPaid && (
        <Totals.TotalsRow
          dataHook={TotalsSectionDataHooks.PayLaterRow}
          title={() => (
            <Text data-hook={TotalsDataHook.TotalsRowTitle}>
              {localeKeys.checkout.orderSummary.amountDueLater.label()}
            </Text>
          )}
          value={() => <Text className={classes.totalRowText}>{checkout.payLater?.total.formattedAmount}</Text>}
        />
      )}
    </Totals.TotalsSection>
  );

  return (
    <section
      className={classes.totalsContainer}
      data-hook={TotalsSectionDataHooks.Section}
      aria-label={localeKeys.checkout.orderTotal.fullSection.ariaLabel()}>
      <Totals dataHook={TotalsSectionDataHooks.TotalsSectionWrapper}>
        <Totals.TotalsRow
          dataHook={TotalsSectionDataHooks.SubtotalRow}
          title={getSubtotalRowTitleText()}
          value={checkout.priceSummary.subtotal.formattedAmount}
        />
        {!!numberOfAdditionalFees && renderAdditionalFees()}
        {!!checkout.appliedCoupon && !checkout.errors.couponError && (
          <Totals.TotalsRow
            dataHook={TotalsSectionDataHooks.AppliedCouponRow}
            title={localeKeys.checkout.order_summary.totals.discount.$value({
              couponCode: shorten(checkout.appliedCoupon.code.toUpperCase(), 10, '...'),
            })}
            value={getCouponRowValueText(checkout.appliedCoupon)}
          />
        )}
        {!!checkout.giftCard && checkout.giftCard.price.amount > 0 && (
          <Totals.TotalsRow
            dataHook={TotalsSectionDataHooks.GiftCardRow}
            title={localeKeys.checkout.order_summary.totals.giftcard()}
            value={`-${checkout.giftCard.price.formattedAmount}`}
          />
        )}
        {checkout.hasShippableItems && (
          <Totals.TotalsRow
            dataHook={TotalsSectionDataHooks.ShippingRow}
            title={getShippingRowTitleText()}
            value={() => (
              <Text
                data-hook={TotalsSectionDataHooks.ShippingRowValue}
                aria-label={!canShipToDestination ? localeKeys.checkout.orderTotal.noShipping.ariaLabel() : undefined}>
                {getShippingRowValueText()}
              </Text>
            )}
          />
        )}
        {!shouldShowTaxBreakdown && (
          <Totals.TotalsRow
            dataHook={TotalsSectionDataHooks.TaxRow}
            title={checkoutSettings.taxName || localeKeys.checkout.order_summary.totals.tax()}
            value={checkout.priceSummary.tax.formattedAmount}
          />
        )}
        {shouldShowTaxBreakdown && renderTaxBreakdowns()}
        <Totals.TotalsSection dataHook={TotalsSectionDataHooks.TotalSection}>
          <Totals.TotalsRow
            dataHook={TotalsSectionDataHooks.TotalRow}
            title={() => (
              <Text className={classes.totalRowText} data-hook={TotalsDataHook.TotalsRowTitle}>
                {localeKeys.checkout.order_summary.totals.total()}
              </Text>
            )}
            value={() => (
              <>
                <Text className={classes.totalRowText}>{getTotal(checkout)}</Text>
                {checkout.lineItems?.[0]?.subscription && !shouldUseNewSubscriptionView && (
                  <Text className={classes.totalRowSubscriptionText} priority={TextPriority.secondary}>
                    {getTotalSubscriptionText(checkout.lineItems[0].subscription, localeKeys)}
                  </Text>
                )}
              </>
            )}
          />
        </Totals.TotalsSection>
        {(isCheckoutPartiallyPaid || shouldSupportFreeTrial) && renderPayNowPayLater()}
      </Totals>
    </section>
  );
};

const getTotalSubscriptionText = (subscription: SubscriptionModel, localeKeys: ILocaleKeys): string => {
  if (subscription.interval > 2) {
    switch (subscription.frequency) {
      case SubscriptionFrequency.DAY: {
        return localeKeys.checkout.page_plan_frequency_description_days_plural_label({
          numberOfFrequencyUnits: subscription.interval,
        });
      }
      case SubscriptionFrequency.WEEK: {
        return localeKeys.checkout.page_plan_frequency_description_weeks_plural_label({
          numberOfFrequencyUnits: subscription.interval,
        });
      }
      case SubscriptionFrequency.MONTH: {
        return localeKeys.checkout.page_plan_frequency_description_months_plural_label({
          numberOfFrequencyUnits: subscription.interval,
        });
      }
      case SubscriptionFrequency.YEAR: {
        return localeKeys.checkout.page_plan_frequency_description_years_plural_label({
          numberOfFrequencyUnits: subscription.interval,
        });
      }
    }
  }
  switch (subscription.frequency) {
    case SubscriptionFrequency.DAY: {
      return subscription.interval === 2
        ? localeKeys.checkout.page_plan_frequency_description_2_days_label()
        : localeKeys.checkout.page_plan_frequency_description_day_singular_label();
    }
    case SubscriptionFrequency.WEEK: {
      return subscription.interval === 2
        ? localeKeys.checkout.page_plan_frequency_description_2_weeks_label()
        : localeKeys.checkout.page_plan_frequency_description_week_singular_label();
    }
    case SubscriptionFrequency.MONTH: {
      return subscription.interval === 2
        ? localeKeys.checkout.page_plan_frequency_description_2_months_label()
        : localeKeys.checkout.page_plan_frequency_description_month_singular_label();
    }
    case SubscriptionFrequency.YEAR: {
      return subscription.interval === 2
        ? localeKeys.checkout.page_plan_frequency_description_2_years_label()
        : localeKeys.checkout.page_plan_frequency_description_year_singular_label();
    }
  }
  /* istanbul ignore next */
  return '';
};
